import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { RadioInput } from '@mm/components';
import { SendlaneIcon } from '@mm/icons';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSendlaneCRUDActions } from '@mm/state/actions/SendlaneCRUDActions';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { selectSendlaneConnections } from '@mm/state/modules/connectionsTableState/selectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as CustomersAiLogo } from 'assets/icons/customersai-logo.svg';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './SendlaneIntegrations.module.css';
import { SendlaneConnectionEditor } from './SendlaneConnectionEditor';
import { SendlaneAccessToken } from '@mm/state/model/SendlaneModel';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

const validConnection = (
  mode: 'all' | 'audience',
  connection: Partial<ConnectionModel>,
) =>
  (mode === 'audience' ? connection.audienceId : !connection.audienceId) &&
  connection.data?.listId;

type Props = {
  accessToken: SendlaneAccessToken;
  controlsDisabled: boolean;
};

export const SendlaneSyncSetup = (props: Props) => {
  const { accessToken, controlsDisabled } = props;
  const availableTags = useSelector((state: StoreState) =>
    tagsOfKinds(state, ['regular']),
  );
  const audiences = useSelector(selectAudienceSorted);
  const sendlaneConnections = useSelector(selectSendlaneConnections);
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const lists = useSelector((state: StoreState) => state.sendlane.lists);
  const [radioValue, setRadioValue] = useState<'all' | 'audience'>(
    sendlaneConnections.find(conn => conn.active)?.audienceId
      ? 'audience'
      : 'all',
  );
  const { getAll } = useTagsCRUDActions();
  const [allConnections, setAllConnections] =
    useState<Partial<ConnectionModel>[]>(sendlaneConnections);
  const { update, create, deleteConnection } = useConnectionCRUDActions();
  const { companyName = 'Customers.ai', isWhitelabelCompany } =
    useWhitelabelConfig();
  const connectionTemplate = useCallback(
    (extra: Partial<ConnectionModel> = {}): Partial<ConnectionModel> => {
      const params = { accessTokenId: accessToken.id };
      return {
        active: true,
        name: 'Sendlane Connection',
        connectionKind: 'sendlane',
        sendlaneParams: params,
        ...extra,
      };
    },
    [accessToken],
  );

  const { getLists } = useSendlaneCRUDActions();

  useEffect(() => {
    getAll(activeFbPageId, true);
    if (lists.length === 0) {
      getLists(activeFbPageId, accessToken.id);
    }
  }, []);

  useEffect(() => {
    setAllConnections(sendlaneConnections);
  }, [sendlaneConnections]);

  const addConnection = () => {
    setAllConnections(prevAllConnections => [
      ...prevAllConnections,
      connectionTemplate(),
    ]);
  };

  const remove = async (index: number) => {
    const connection = allConnections[index];
    if (connection.id) {
      await deleteConnection(activeFbPageId, connection as ConnectionModel);
    }
    setAllConnections(prevAllConnections =>
      prevAllConnections.filter(conn => conn !== connection),
    );
  };

  const updateOrEdit = async (
    index: number,
    updateData: Partial<ConnectionModel>,
  ) => {
    const connection = allConnections[index];
    if (connection.id) {
      const updatedConnection = await update(activeFbPageId, {
        id: connection.id,
        ...updateData,
      });
      if (updatedConnection) {
        setAllConnections(prevAllConnections =>
          Object.assign([], prevAllConnections, { [index]: updatedConnection }),
        );
      }
    } else if (validConnection(radioValue, { ...connection, ...updateData })) {
      const createdConnection = await create(activeFbPageId, {
        ...connection,
        ...updateData,
      });
      if (createdConnection) {
        setAllConnections(prevAllConnections =>
          Object.assign([], prevAllConnections, { [index]: createdConnection }),
        );
      }
    } else {
      setAllConnections(prevAllConnections =>
        Object.assign([], prevAllConnections, {
          [index]: { ...connection, ...updateData },
        }),
      );
    }
  };

  const selectedLists = useMemo(
    () =>
      allConnections.reduce((acc, conn) => {
        if (conn.data?.listId && (!conn.id || conn.audienceId)) {
          acc[conn.data.listId] = true;
        }
        return acc;
      }, {}),
    [allConnections],
  );

  const selectedAudiences = useMemo(
    () =>
      allConnections.reduce((acc, conn) => {
        if (conn.audienceId) {
          acc[conn.audienceId] = true;
        }
        return acc;
      }, {}),
    [allConnections],
  );

  const selectableLists = (conn?: Partial<ConnectionModel>) =>
    lists.filter(
      list => list.id === conn?.data?.listId || !selectedLists[list.id],
    );

  const selectableAudiences = (conn?: Partial<ConnectionModel>) =>
    audiences.filter(
      aud => aud.id === conn?.audienceId || !selectedAudiences[aud.id],
    );

  const canAdd = selectableLists().length && selectableAudiences().length;
  // all contacts webhook is always there, so 2 connections means 1 all contacts + 1 audience based
  const multipleConnections = allConnections.length > 2;

  const onChangeType = (type: 'all' | 'audience') => {
    const cond = conn =>
      type === 'all'
        ? conn.id && !conn.audienceId
        : conn.audienceId || !conn.id;
    return async () => {
      const promises = allConnections.map((conn, index) => {
        if (cond(conn)) {
          return updateOrEdit(index, { active: true });
        }
        return updateOrEdit(index, { active: false });
      });
      if (allConnections.length === 1) {
        // just one connection is only possible if the user
        // has only configured no audiences setup.
        addConnection();
      } else {
        await Promise.all(promises);
      }
      setRadioValue(type);
    };
  };

  return (
    <div
      className={classNames(styles.box, {
        [styles.boxDisabled]: controlsDisabled,
      })}
    >
      <div className={styles.boxWithHelp}>
        <div className={styles.boxContent}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Contact sync
          </div>
        </div>
      </div>
      <div className={styles.radioInputs}>
        <RadioInput
          name="all"
          value="all"
          label={`Sync all ${companyName} contacts to Sendlane`}
          onChange={onChangeType('all')}
          checked={radioValue === 'all'}
        />
        <RadioInput
          name="audience"
          value="audience"
          label="Choose an audience to sync from"
          onChange={onChangeType('audience')}
          checked={radioValue === 'audience'}
        />
      </div>
      <div>
        <PrimaryButton
          className={classNames(
            'integrations__header-add-button-secondary',
            'integrations__header-add-button-action',
            'integrations__header-button-no-margin',
          )}
          onClick={_ => getLists(activeFbPageId, accessToken.id, true)}
          leftIcon={<SyncOutlined />}
        >
          Refresh lists
        </PrimaryButton>
      </div>
      <div className={styles.gridContainer}>
        {radioValue === 'audience' ? (
          <>
            <div className={styles.gridAudiences}>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                {!isWhitelabelCompany && (
                  <CustomersAiLogo className={styles.gridIcon} />
                )}{' '}
                {companyName}
                Audience
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)} />
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                <div
                  className={classNames(styles.gridIcon, styles.sendlaneIcon)}
                >
                  <SendlaneIcon size={12} />
                </div>
                Sendlane List
              </div>
              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                {/* Add tags to contacts in Sendlane #Will be added with tags feature */}
              </div>

              <div className={classNames(styles.gridColumn, styles.firstRow)}>
                Actions
              </div>
              {allConnections.map((conn, idx) => {
                if (!conn.id || conn.audienceId) {
                  const key = `connection-${idx}`;
                  return (
                    <SendlaneConnectionEditor
                      key={key}
                      canDelete={multipleConnections}
                      connection={conn}
                      withAudiences={true}
                      lists={selectableLists(conn)}
                      audiences={selectableAudiences(conn)}
                      update={props => updateOrEdit(idx, props)}
                      remove={() => remove(idx)}
                      activeFbPageId={activeFbPageId}
                      availableTags={availableTags}
                    />
                  );
                }
                return null;
              })}
            </div>
            {canAdd ? (
              <PrimaryButton
                className={classNames(
                  'integrations__header-add-button-secondary',
                  'integrations__header-add-button-action',
                  'integrations__header-button-no-margin',
                )}
                onClick={addConnection}
                leftIcon={<PlusOutlined />}
              >
                Add
              </PrimaryButton>
            ) : null}
          </>
        ) : (
          <div className={styles.gridAll}>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              <div className={classNames(styles.gridIcon, styles.sendlaneIcon)}>
                <SendlaneIcon size={12} />
              </div>
              Sendlane List
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              {/* Add tags to contacts in Sendlane #Will be added with tags feature */}
            </div>
            <div className={classNames(styles.gridColumn, styles.firstRow)}>
              Actions
            </div>
            {allConnections.map((conn, idx) => {
              const key = `connection-${idx}`;
              if (conn.id && !conn.audienceId) {
                return (
                  <SendlaneConnectionEditor
                    key={key}
                    canDelete={false}
                    connection={conn}
                    withAudiences={false}
                    lists={lists}
                    audiences={audiences}
                    update={props => updateOrEdit(idx, props)}
                    activeFbPageId={activeFbPageId}
                    availableTags={availableTags}
                  />
                );
              }
              return null;
            })}
          </div>
        )}
        <div className={styles.tagsNote}>
          All tags will be added to a tag group “{companyName}”
        </div>
      </div>
    </div>
  );
};
